import { Logger } from 'loglevel';
import { User } from '@auth0/auth0-react';
import { ENV } from '../config/env';

export class KeyAuthService {
  constructor(private readonly logger: Logger) {}

  private readonly apiBaseUrl = ENV.BACK_EDI_URL;
  private readonly auth0Domain = ENV.CLOUDID_DOMAIN;

  /**
   * Exchange a key for an access token
   * @param key The access key received from URL parameter
   * @returns The access token
   * @throws Error if the key is invalid, expired, or server error occurs
   */
  async exchangeKeyForToken(key: string): Promise<string> {
    this.logger.info('🔑 Attempting to exchange key for access token');

    if (!key || key.trim().length === 0) {
      this.logger.error('Key authentication failed: empty key provided');
      throw new Error("La clé d'authentification est invalide ou manquante");
    }

    const apiUrl = `${this.apiBaseUrl}/OAuthClient/Url/GetAccessTokenFromKey?key=${encodeURIComponent(key)}`;

    try {
      this.logger.debug('Calling key authentication API:', { url: apiUrl });

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      this.logger.info('📥 Key authentication API response:', {
        status: response.status,
        statusText: response.statusText,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.logger.warn('Key authentication failed: invalid key (401)');
          throw new Error("La clé d'authentification est invalide");
        }

        if (response.status === 403) {
          this.logger.warn('Key authentication failed: expired key (403)');
          throw new Error("La clé d'authentification a expiré");
        }

        if (response.status === 404) {
          this.logger.warn('Key authentication failed: endpoint not found (404)');
          throw new Error("Service d'authentification non disponible");
        }

        throw new Error('Key authentication failed with server error');
      }

      const accessToken = await response.json();

      if (!accessToken) {
        this.logger.error('Key authentication response missing accessToken');
        throw new Error("La réponse du serveur ne contient pas de token d'accès");
      }

      this.logger.info('✅ Key authentication successful');
      return accessToken;
    } catch (error) {
      if (error instanceof Error) {
        this.logger.error('Key authentication error:', {
          message: error.message,
          name: error.name,
        });
        throw error;
      }

      this.logger.error('Unknown key authentication error:', error);
      throw new Error("Une erreur inattendue s'est produite lors de l'authentification");
    }
  }

  /**
   * Fetch user information from Auth0 using an access token
   * @param accessToken The access token obtained from key exchange
   * @returns User information from Auth0
   * @throws Error if the token is invalid or server error occurs
   */
  async getUserInfo(accessToken: string): Promise<User> {
    const authDomain: string = this.auth0Domain;
    this.logger.info(`👤 Fetching user information from Auth0 [${authDomain}]`);

    if (!accessToken || accessToken.trim().length === 0) {
      this.logger.error('Cannot fetch user info: empty access token');
      throw new Error("Le token d'accès est invalide");
    }

    // Remove https:// prefix if present in domain configuration
    const domain = this.auth0Domain?.replace(/^https?:\/\//, '');
    const userInfoUrl = `https://${domain}/userinfo`;

    try {
      this.logger.debug('Calling Auth0 userinfo endpoint:', { url: userInfoUrl });

      const response = await fetch(userInfoUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      this.logger.info('📥 Auth0 userinfo response:', {
        status: response.status,
        statusText: response.statusText,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.logger.warn('User info fetch failed: invalid or expired token (401)');
          throw new Error("Le token d'accès est invalide ou expiré");
        }

        throw new Error('Failed to fetch user information from Auth0');
      }

      const userInfo: User = await response.json();

      this.logger.info('✅ User information retrieved successfully');
      this.logger.debug('User info:', {
        sub: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name,
      });

      return userInfo;
    } catch (error) {
      if (error instanceof Error) {
        this.logger.error('User info fetch error:', {
          message: error.message,
          name: error.name,
        });
        throw error;
      }

      this.logger.error('Unknown user info fetch error:', error);
      throw new Error(
        "Une erreur inattendue s'est produite lors de la récupération des informations utilisateur",
      );
    }
  }
}
